import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { getBelt, getTheme, calcRating, calcCatScore, getBeltLevel, getBeltLevelProgress, makeAvatarSvg, makeInitialsSvg, getFlagSvg } from '../lib/helpers'
import { FRONT_STAT_KEYS } from '../lib/constants'

export default function PublicProfilePage() {
  const { passportId } = useParams()
  const [fighter, setFighter] = useState(null)
  const [skills, setSkills] = useState([])
  const [competitions, setCompetitions] = useState([])
  const [categories, setCategories] = useState([])
  const [techniques, setTechniques] = useState([])
  const [club, setClub] = useState(null)
  const [loading, setLoading] = useState(true)
  const [flipped, setFlipped] = useState(false)

  useEffect(() => {
    async function load() {
      const { data: f } = await supabase
        .from('fighters').select('*').eq('passport_id', passportId).single()

      if (!f) { setLoading(false); return }

      // If profile is private, don't load
      if (!f.profile_public) { setFighter({ private: true }); setLoading(false); return }

      const [sk, comps, cats, techs, clubData] = await Promise.all([
        supabase.from('fighter_skills').select('*').eq('fighter_id', f.id),
        supabase.from('competitions').select('*').eq('fighter_id', f.id).order('competition_date', { ascending: false }),
        supabase.from('skill_categories').select('*').eq('club_id', f.club_id).order('display_order'),
        supabase.from('techniques').select('*').order('display_order'),
        supabase.from('clubs').select('*').eq('id', f.club_id).single(),
      ])

      setFighter(f)
      setSkills(sk.data || [])
      setCompetitions(comps.data || [])
      setCategories(cats.data || [])
      setTechniques(techs.data || [])
      setClub(clubData.data)
      setLoading(false)
    }
    load()
  }, [passportId])

  if (loading) return <Shell><div style={{ color: '#888', textAlign: 'center', padding: '3rem' }}>Loading...</div></Shell>

  if (!fighter) return (
    <Shell>
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🥋</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: '#1a1a1a', marginBottom: 8 }}>Profile not found</div>
        <div style={{ fontSize: 13, color: '#888' }}>This passport ID doesn't exist.</div>
      </div>
    </Shell>
  )

  if (fighter.private) return (
    <Shell>
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: '#1a1a1a', marginBottom: 8 }}>Private profile</div>
        <div style={{ fontSize: 13, color: '#888' }}>This fighter's profile is private.</div>
      </div>
    </Shell>
  )

  const belt = getBelt(fighter)
  const theme = getTheme(fighter, competitions)
  const rating = calcRating(fighter, skills, competitions)
  const level = getBeltLevel(fighter)
  const levelProgress = getBeltLevelProgress(fighter)
  const flagSvg = getFlagSvg(fighter.country || 'HR')

  const frontStats = FRONT_STAT_KEYS.map(({ label, cat }) => {
    const catData = categories.find(c => c.name === cat)
    const techIds = catData ? techniques.filter(t => t.category_id === catData.id).map(t => t.id) : []
    return { label, value: calcCatScore(skills, techIds) }
  })

  const backStats = categories.map(cat => {
    const techIds = techniques.filter(t => t.category_id === cat.id).map(t => t.id)
    return { name: cat.name, value: calcCatScore(skills, techIds) }
  })

  const faceHtml = fighter.photo_url ? null
    : fighter.avatar_style === 'initials'
      ? makeInitialsSvg(fighter.first_name, fighter.last_name, belt.a1, belt.a2, 200)
      : makeAvatarSvg(fighter.avatar_seed || 1, belt.a1, belt.a2, 200)

  const goldCount = competitions.filter(c => c.medal === 'gold').length
  const silverCount = competitions.filter(c => c.medal === 'silver').length
  const bronzeCount = competitions.filter(c => c.medal === 'bronze').length

  return (
    <Shell noIndex={!fighter.profile_public}>
      <style>{`@keyframes shine{0%{left:-80%}60%{left:130%}100%{left:130%}}`}</style>

      {/* Club header */}
      {club && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1.5rem', padding: '10px 14px', background: '#fff', borderRadius: 10, border: '1px solid #eee' }}>
          {club.logo_url
            ? <img src={club.logo_url} alt={club.name} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
            : <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#1860a8', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 16 }}>{club.name[0]}</div>
          }
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{club.name}</div>
            <div style={{ fontSize: 11, color: '#888' }}>Fighter profile</div>
          </div>
          <div style={{ marginLeft: 'auto', fontFamily: 'monospace', fontSize: 11, color: '#aaa' }}>{fighter.passport_id}</div>
        </div>
      )}

      {/* Card */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
        <div onClick={() => setFlipped(f => !f)} style={{ width: 240, height: 340, perspective: 900, cursor: 'pointer' }}>
          <div style={{
            width: '100%', height: '100%', position: 'relative',
            transformStyle: 'preserve-3d',
            transition: 'transform 0.55s cubic-bezier(0.4,0,0.2,1)',
            transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
          }}>
            {/* Front */}
            <div style={{
              position: 'absolute', width: '100%', height: '100%', borderRadius: 16,
              overflow: 'hidden', backfaceVisibility: 'hidden',
              background: theme.bg, border: `2.5px solid ${theme.border}`, color: theme.color
            }}>
              <div style={{ position: 'absolute', top: 0, left: '-80%', width: '40%', height: '100%', background: 'linear-gradient(105deg,transparent 30%,rgba(255,255,255,0.22) 50%,transparent 70%)', animation: 'shine 4s infinite', pointerEvents: 'none', zIndex: 10 }} />
              <div style={{ position: 'relative', height: '58%', overflow: 'hidden' }}>
                {fighter.photo_url
                  ? <img src={fighter.photo_url} alt={fighter.first_name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }} />
                  : <div dangerouslySetInnerHTML={{ __html: faceHtml }} style={{ width: '100%', height: '100%' }} />
                }
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom,transparent 50%,rgba(0,0,0,0.35) 100%)' }} />
                <div style={{ position: 'absolute', top: 10, left: 10 }}>
                  <div style={{ fontSize: 44, fontWeight: 700, lineHeight: 1, textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>{rating}</div>
                  <div style={{ fontSize: 9, letterSpacing: '0.1em', opacity: 0.85 }}>OVR</div>
                </div>
                <div style={{ position: 'absolute', top: 10, right: 10 }}>
                  <div style={{ width: 28, height: 18, borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.15)' }} dangerouslySetInnerHTML={{ __html: flagSvg }} />
                </div>
              </div>
              <div style={{ padding: '7px 10px 8px' }}>
                <div style={{ fontSize: 13, fontWeight: 700, textAlign: 'center', letterSpacing: '0.06em', marginBottom: 2 }}>
                  {fighter.first_name.toUpperCase()} {fighter.last_name.toUpperCase()}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 3 }}>
                  <span style={{ fontSize: 9, opacity: 0.75 }}>{belt.short} · Lv.{level}</span>
                </div>
                <div style={{ height: 3, background: 'rgba(0,0,0,0.2)', borderRadius: 2, marginBottom: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${levelProgress * 100}%`, height: '100%', background: 'currentColor', opacity: 0.7, borderRadius: 2 }} />
                </div>
                <div style={{ height: '0.5px', background: 'currentColor', opacity: 0.2, marginBottom: 4 }} />
                <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                  {frontStats.map(s => (
                    <div key={s.label} style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1 }}>{s.value}</div>
                      <div style={{ fontSize: 7, opacity: 0.65, textTransform: 'uppercase' }}>{s.label}</div>
                    </div>
                  ))}
                </div>
                {(goldCount + silverCount + bronzeCount) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 3, marginTop: 5 }}>
                    {Array(Math.min(goldCount,4)).fill(0).map((_,i) => <div key={'g'+i} style={{ width: 8, height: 8, borderRadius: '50%', background: '#d4a017' }} />)}
                    {Array(Math.min(silverCount,3)).fill(0).map((_,i) => <div key={'s'+i} style={{ width: 8, height: 8, borderRadius: '50%', background: '#909090' }} />)}
                    {Array(Math.min(bronzeCount,3)).fill(0).map((_,i) => <div key={'b'+i} style={{ width: 8, height: 8, borderRadius: '50%', background: '#a05020' }} />)}
                  </div>
                )}
              </div>
            </div>

            {/* Back */}
            <div style={{
              position: 'absolute', width: '100%', height: '100%', borderRadius: 16,
              overflow: 'hidden', backfaceVisibility: 'hidden', transform: 'rotateY(180deg)',
              background: theme.bg, border: `2.5px solid ${theme.border}`, color: theme.color
            }}>
              <div style={{ padding: '12px', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: 10, fontWeight: 700, textAlign: 'center', marginBottom: 2 }}>{fighter.first_name.toUpperCase()} {fighter.last_name.toUpperCase()}</div>
                <div style={{ fontSize: 8, textAlign: 'center', opacity: 0.6, marginBottom: 6 }}>{belt.n} · Level {level}/10 · {fighter.sessions_count || 0} sessions</div>
                <div style={{ height: '0.5px', background: 'currentColor', opacity: 0.2, marginBottom: 6 }} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {backStats.map(s => (
                    <div key={s.name} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: 7.5, opacity: 0.7, flex: '0 0 58px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                      <div style={{ flex: 1, height: 4, background: 'rgba(0,0,0,0.18)', borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ width: `${s.value}%`, height: '100%', background: 'currentColor', opacity: 0.75, borderRadius: 2 }} />
                      </div>
                      <span style={{ fontSize: 8, fontWeight: 600, opacity: 0.85, minWidth: 16, textAlign: 'right' }}>{s.value}</span>
                    </div>
                  ))}
                </div>
                {competitions.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    <div style={{ fontSize: 7.5, opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Competitions</div>
                    {competitions.slice(0, 3).map((c, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: c.medal === 'gold' ? '#d4a017' : c.medal === 'silver' ? '#909090' : '#a05020', flexShrink: 0 }} />
                        <span style={{ fontSize: 7.5, opacity: 0.85, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.tournament_name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <p style={{ textAlign: 'center', fontSize: 12, color: '#aaa', marginBottom: '1.5rem' }}>Tap card to flip</p>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: '1.5rem' }}>
        {[
          { label: 'Sessions', value: fighter.sessions_count || 0 },
          { label: 'Belt level', value: `Lv.${level}` },
          { label: 'Medals', value: goldCount + silverCount + bronzeCount },
        ].map(s => (
          <div key={s.label} style={{ background: '#fff', borderRadius: 12, padding: 12, textAlign: 'center', border: '1px solid #eee' }}>
            <div style={{ fontSize: 18, fontWeight: 600, color: '#1a1a1a' }}>{s.value}</div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Competition history */}
      {competitions.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 12, padding: '1rem 1.25rem', border: '1px solid #eee' }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10, color: '#1a1a1a' }}>Competition history</div>
          {competitions.map((c, i) => {
            const col = c.medal === 'gold' ? '#d4a017' : c.medal === 'silver' ? '#909090' : '#a05020'
            return (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: i < competitions.length - 1 ? '1px solid #f4f4f4' : 'none' }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: col, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{c.tournament_name}</div>
                  {c.weight_class && <div style={{ fontSize: 11, color: '#888' }}>{c.weight_class}</div>}
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1a1a', textTransform: 'capitalize' }}>
                  {c.medal} · {c.wins} win{c.wins !== 1 ? 's' : ''}
                  {c.submission_used ? ` · ${c.submission_used}` : ''}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <div style={{ textAlign: 'center', marginTop: '2rem', fontSize: 11, color: '#ccc' }}>
        Powered by MyAthleteCard · {fighter.passport_id}
      </div>
    </Shell>
  )
}

function Shell({ children, noIndex }) {
  return (
    <div style={{ minHeight: '100vh', background: '#f4f6fb', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      {noIndex && (
        <script dangerouslySetInnerHTML={{ __html: `document.head.innerHTML += '<meta name="robots" content="noindex,nofollow">'` }} />
      )}
      <div style={{ background: '#fff', borderBottom: '1px solid #eee', padding: '0 1.25rem', height: 48, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 18 }}>🥋</span>
        <span style={{ fontWeight: 700, fontSize: 14, color: '#1a1a1a' }}>MyAthleteCard</span>
      </div>
      <main style={{ maxWidth: 480, margin: '0 auto', padding: '1.5rem 1rem' }}>
        {children}
      </main>
    </div>
  )
}
